import { Injectable, inject } from "@angular/core";
import { IEndpoint, IStream, StreamTypes } from "@auvious/rtc";
import { Subscription } from "rxjs";

import {
  AgentParam,
  DEFAULT_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS,
  MAX_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS,
  MIN_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS,
  secondsSetting
} from "../models";
import { AppConfigService } from "./app.config.service";
import { ConferenceService } from "./conference.service";
import { DeepfakeService, kindsFor } from "./deepfake.service";
import { debug, debugError } from "./utils";

/** A video element that has decoded at least one frame; audio elements always pass. */
function hasFrame(element: HTMLMediaElement): boolean {
  return !(element instanceof HTMLVideoElement) || element.videoWidth > 0;
}

/** Where the check is filed; read at fire time, the conference may not exist at attach. */
export interface IDeepfakeAutoCheckContext {
  interactionId?: string;
  conferenceId?: () => string | undefined;
}

/**
 * The automatic media-authenticity check.
 *
 * Policy, in the admin's words: some seconds after a customer's stream
 * appears, check it; forget it if the stream goes away first; a new stream
 * from the same participant (camera switch, reconnect, second camera, screen
 * share) starts its own countdown. Every agent in the room runs this, so the
 * one with the smallest endpoint id does the actual check and the others
 * only watch — a supervisor listening in would otherwise double every scan.
 * The election is repeated when the timer fires, so an owner who has left
 * by then hands over instead of leaving the customer unchecked.
 *
 * Runs once per stream. It never retries a check that came back with an
 * error and never re-checks a stream, so a call costs at most one check per
 * customer stream, however long it lasts.
 */
@Injectable()
export class DeepfakeAutoCheckService {
  private config = inject(AppConfigService);
  private conference = inject(ConferenceService);
  private deepfake = inject(DeepfakeService);

  private subscriptions: Subscription[] = [];
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Stream id -> its participant, to drop every countdown of someone who left. */
  private owners = new Map<string, string>();
  /** Streams already checked (or skipped at fire time), so nothing fires twice. */
  private done = new Set<string>();
  /** Streams given a second countdown because the tile had no frame yet. */
  private retried = new Set<string>();
  private context: IDeepfakeAutoCheckContext = {};

  public get isEnabled(): boolean {
    return (
      this.deepfake.isEnabled &&
      this.config.agentParamEnabled(AgentParam.DEEPFAKE_AUTO_CHECK_ENABLED)
    );
  }

  public get includesScreens(): boolean {
    return this.config.agentParamEnabled(
      AgentParam.DEEPFAKE_AUTO_CHECK_SCREENS
    );
  }

  /**
   * The wait, in ms. Config is an untyped map, so a missing or garbage value
   * falls back to the default rather than firing at once (`setTimeout(NaN)`).
   */
  public get delayMs(): number {
    return (
      secondsSetting(
        this.config.agentParam(AgentParam.DEEPFAKE_AUTO_CHECK_DELAY_SECONDS),
        MIN_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS,
        MAX_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS,
        DEFAULT_DEEPFAKE_AUTO_CHECK_DELAY_SECONDS
      ) * 1000
    );
  }

  /** Whether a countdown is running for this stream (for tests and the tile). */
  public isScheduled(streamId: string): boolean {
    return this.timers.has(streamId);
  }

  /**
   * Starts watching the room. Cheap when the policy is off for this
   * application: nothing is subscribed, so a customer's browser or an agent
   * without the feature pays nothing.
   */
  public attach(context: IDeepfakeAutoCheckContext = {}): void {
    this.detach();

    if (!this.isEnabled) return;

    this.context = context;

    this.subscriptions = [
      this.conference.streamAdded$.subscribe((stream) =>
        this.onStreamAdded(stream)
      ),
      this.conference.streamRemoved$.subscribe((stream) =>
        this.cancel(stream.id)
      ),
      this.conference.endpointLeft$.subscribe((endpoint) =>
        this.cancelForEndpoint(endpoint)
      )
    ];
  }

  /** Leaving the room: `streamRemoved` does not fire for our own leave. */
  public detach(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.owners.clear();
    this.done.clear();
    this.retried.clear();
    this.context = {};
  }

  private onStreamAdded(stream: IStream): void {
    if (!this.shouldWatch(stream)) return;

    // a stream id is fresh per publish, so a new id is the "new stream" that
    // gets its own countdown; the same id again is rtc recovering the stream
    // we already know (streamRecovered re-emits streamAdded) — not a new one
    if (this.done.has(stream.id) || this.timers.has(stream.id)) return;

    debug("deepfake policy: countdown for stream", stream.id, stream.type);

    this.owners.set(stream.id, stream.originator?.endpoint);
    this.schedule(stream);
  }

  private schedule(stream: IStream): void {
    this.timers.set(
      stream.id,
      setTimeout(() => {
        this.timers.delete(stream.id);
        this.fire(stream);
      }, this.delayMs)
    );
  }

  private shouldWatch(stream: IStream): boolean {
    if (stream.originator?.endpoint === this.conference.myself?.endpoint) {
      return false;
    }

    if (stream.type === StreamTypes.SCREEN && !this.includesScreens) {
      return false;
    }

    // only customers, and only where an agent could also check by hand
    return (
      this.deepfake.supported(this.participantOf(stream)) &&
      this.deepfake.canCheckStream(stream.type === StreamTypes.SCREEN)
    );
  }

  private fire(stream: IStream): void {
    if (this.done.has(stream.id)) return;

    // the room may have changed during the countdown
    if (!this.isEnabled || !this.conference.getStream(stream.id)) return;

    const element = this.conference.getElementById(stream.id);

    // No frame on the tile yet (a slow connection, a track still muted at
    // our end, the tile not even bound): a still of it would be empty. Wait
    // one more countdown rather than fail the check on a stream that is
    // merely late; if it is still blank the second time, the check runs and
    // reports that honestly.
    if ((!element || !hasFrame(element)) && !this.retried.has(stream.id)) {
      this.retried.add(stream.id);
      debug("deepfake policy: no frame yet, waiting once more", stream.id);
      this.schedule(stream);
      return;
    }

    this.done.add(stream.id);

    if (!this.isOwner()) return;

    // Two agents can both win the election while joining (neither sees the
    // other yet), and a reload forgets `done`. Any check on this stream, by
    // anyone and whatever its state, is listed by the ai and so is not run
    // again. What is left is the listing's own delay; for it, the ai answers
    // a duplicate automatic check with the existing one (`existing`) instead
    // of scanning again.
    if (
      Object.values(this.deepfake.checks()).some(
        (check) => check.targetStreamId === stream.id
      )
    ) {
      return;
    }

    const isScreen = stream.type === StreamTypes.SCREEN;
    const source = element?.srcObject;

    if (
      !element ||
      !kindsFor(
        this.deepfake.checkMode,
        source instanceof MediaStream ? source : null,
        isScreen
      )
    ) {
      debug("deepfake policy: nothing to check on stream", stream.id);
      return;
    }

    this.deepfake
      .check(
        element,
        this.participantOf(stream) ?? stream.originator,
        {
          interactionId: this.context.interactionId,
          conferenceId: this.context.conferenceId?.()
        },
        { streamId: stream.id, isScreen, trigger: "policy" }
      )
      .catch((ex) => debugError(ex));
  }

  /**
   * One agent per room does the checking: the smallest endpoint id among
   * the agents present. Every client sees the same participants, so they
   * all agree without talking to each other.
   */
  private isOwner(): boolean {
    const mine = this.conference.myself?.endpoint;
    const agents = this.conference
      .getParticipantAgents()
      .map((agent) => agent.endpoint)
      .filter(Boolean)
      .sort();

    return !agents.length || agents[0] === mine;
  }

  private participantOf(stream: IStream): IEndpoint | undefined {
    const endpoint = stream.originator?.endpoint;

    return this.conference.getParticipants()?.[endpoint] ?? stream.originator;
  }

  private cancel(streamId: string): void {
    const timer = this.timers.get(streamId);

    if (timer) {
      clearTimeout(timer);
      this.timers.delete(streamId);
      debug("deepfake policy: countdown cancelled for stream", streamId);
    }

    this.owners.delete(streamId);
    this.done.delete(streamId);
    this.retried.delete(streamId);
  }

  private cancelForEndpoint(endpoint: IEndpoint): void {
    Array.from(this.owners.entries())
      .filter(([, owner]) => owner === endpoint?.endpoint)
      .forEach(([streamId]) => this.cancel(streamId));
  }
}
